import { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, Video, Download } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Reveal, { Kicker } from "@/components/Reveal";
import Seo from "@/components/Seo";

const TYPE_ICONS = { text: FileText, image: ImageIcon, video: Video };

export default function Press() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/press")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadText = (item) => {
    const title = lang === "de" ? item.title_de : item.title_en;
    const body = lang === "de" ? item.description_de : item.description_en;
    const blob = new Blob([`${title}\n\n${body}\n\nLatschenwirt · Buchhöhstraße 122 · 5084 Großgmain · office@latschenwirt.at`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pressetext-krampus-show.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <main data-testid="press-page" className="pt-28 md:pt-36 pb-24">
      <Seo path="/presse" title="Presse | Latschenwirt Krampus-Show 2026" titleEn="Press | Latschenwirt Krampus Show 2026" description="Pressemeldungen, Bild- und Videomaterial sowie Pressekontakt zur Krampus-Show 2026 in Großgmain bei Salzburg." descriptionEn="Press releases, image and video material and press contact for the Krampus Show 2026 in Grossgmain near Salzburg." />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="mb-16 text-center">
          <Kicker>{t("press_kicker")}</Kicker>
          <h1 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-6 ember-glow">{t("press_title")}</h1>
          <p className="text-hell-smoke max-w-2xl md:mx-auto text-base md:text-lg">{t("press_sub")}</p>
        </Reveal>
        {loading ? (
          <p className="text-hell-smoke font-cinzel tracking-widest">…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {items.map((item, i) => {
              const Icon = TYPE_ICONS[item.type] || FileText;
              const title = lang === "de" ? item.title_de : item.title_en;
              const desc = lang === "de" ? item.description_de : item.description_en;
              return (
                <Reveal key={item.press_id} delay={(i % 2) * 0.1}>
                  <div data-testid={`press-item-${item.press_id}`}
                    className="border border-white/10 bg-hell-coal p-8 hover:border-hell-gold/40 transition-colors duration-500 h-full flex flex-col">
                    <Icon className="w-6 h-6 text-hell-ember mb-5" />
                    <h2 className="font-display text-2xl text-hell-bone mb-3">{title}</h2>
                    <p className="text-sm text-hell-smoke leading-relaxed mb-6 flex-1">{desc}</p>
                    {item.type === "text" ? (
                      <button data-testid={`press-download-${item.press_id}`} onClick={() => downloadText(item)}
                        className="inline-flex items-center gap-2 font-cinzel text-xs tracking-[0.2em] uppercase text-hell-gold hover:text-hell-ember transition-colors self-start">
                        <Download className="w-4 h-4" />{t("press_download")}
                      </button>
                    ) : (
                      <a data-testid={`press-download-${item.press_id}`} href={item.file_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 font-cinzel text-xs tracking-[0.2em] uppercase text-hell-gold hover:text-hell-ember transition-colors self-start">
                        <Download className="w-4 h-4" />{t("press_download")}
                      </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
